import get from 'lodash/get';
import { loadLoggedInAccount } from 'modules/accounts';
import { Cognito } from './services';
import {
  USER_LOADED,
  PASSWORD_REQUIRED,
  PASSWORD_NOT_REQUIRED,
  LOGIN_SUCCESS,
  LOGIN_ERROR,
  completeNewPassword as apiCompleteNewPassword,
  login as apiLogin,
  logout as apiLogout,
  signUp as apiSignUp,
  requestProducerApproval as apiProducerApproval,
  verifyEmail as apiVerifyEmail,
  resetPassword as apiResetPassword,
  forgotPassword as apiForgotPasword,
  changePassword as apiChangePassword,
  resendVerificationEmail as apiResendVerificationEmail,
  uploadApplicationAudioClip as apiUploadAudioClip,
} from './api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const login = apiLogin;
export const logout = createAsyncThunk(
  'auth/logout',
  async _ => await apiLogout(),
);
export const signUp = createAsyncThunk(
  'auth/signUp',
  async account => await apiSignUp(account),
);
export const producerApproving = createAsyncThunk(
  'auth/producerApproving',
  async producer => await apiProducerApproval(producer),
);
export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async ({ email, code }) => await apiVerifyEmail(email, code),
);
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ code, password, sub }, { rejectWithValue }) => {
    try {
      return await apiResetPassword({ code, password, sub });
    } catch (e) {
      throw rejectWithValue(Object.values(e)[0].detail);
    }
  }
);

export const completeNewPassword = createAsyncThunk(
  'auth/completeNewPassword',
  async ({ user, password, email }) =>
    await apiCompleteNewPassword(user, password, email),
);
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async email => await apiForgotPasword(email),
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ oldPassword, newPassword }) =>
    await apiChangePassword(oldPassword, newPassword),
);

export const resendVerificationEmail = createAsyncThunk(
  'auth/resendVerificationEmail',
  async email => await apiResendVerificationEmail(email),
);

export const uploadApprovalAudioClip = createAsyncThunk(
  'auth/uploadApprovalAudioClip',
  async ({ file, context }) => await apiUploadAudioClip(file, context),
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    authenticated: false,
    passwordResetRequired: false,
    cognitoUser: null,
    loading: false,
    emailError: false,
    loginError: false,
    signupError: false,
    signupSuccess: false,
    pendingVerification: false,
    verificationError: false,
    verificationSuccess: false,
    resetPasswordError: false,
    resetPasswordSuccess: false,
    changePasswordError: false,
    changePasswordSuccess: false,
    forgotPasswordSuccess: false,
    forgotPasswordError: false,
    resendVerificationEmailSuccess: false,
    resendVerificationEmailError: false,
    producerApprovalError: false,
    producerApprovalSuccess: false,
    email: null,
  },
  reducers: {
    clearSignUpStatus(state, _) {
      state.signupSuccess = false;
      state.signupError = false;
    },
    clearResetPasswordStatus(state) {
      state.resetPasswordSuccess = false;
      state.resetPasswordError = false;
    },
    clearForgotPasswordStatus(state) {
      state.forgotPasswordSuccess = false;
      state.forgotPasswordError = false;
    },
    clearChangePasswordStatus(state) {
      state.changePasswordSuccess = false;
      state.changePasswordError = false;
    },
    clearResendVerificationEmailStatus(state) {
      state.resendVerificationEmailSuccess = false;
      state.resendVerificationEmailError = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(completeNewPassword.fulfilled, (state, _) => {
        state.completeNewPasswordSuccess = true;
        state.completeNewPasswordError = false;
      })
      .addCase(completeNewPassword.rejected, (state, action) => {
        const error = get(action, 'error');

        state.completeNewPasswordSuccess = false;
        state.completeNewPasswordError = Cognito.getCognitoErrorMessage(error);
      })
      .addCase(loadLoggedInAccount.fulfilled, (state, _) => {
        state.loginError = false;
      })
      .addCase(signUp.fulfilled, (state, _) => {
        state.loading = false;
        state.authenticated = false;
        state.signupError = false;
        state.signupSuccess = true;
        state.pendingVerification = true;
      })
      .addCase(signUp.rejected, (state, action) => {
        const error = get(action, 'error');

        state.signupSuccess = false;
        state.signupError = Cognito.getCognitoErrorMessage(error, true);
      })
      .addCase(producerApproving.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(producerApproving.fulfilled, (state, _) => {
        state.producerApprovalSuccess = true;
        state.loading = false;
      })
      .addCase(producerApproving.rejected, (state, action) => {
        state.producerApprovalError = get(action, 'error');
        state.loading = false;
      })
      .addCase(resetPassword.fulfilled, (state, _) => {
        state.resetPasswordError = false;
        state.resetPasswordSuccess = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordSuccess = false;
        state.resetPasswordError = action.payload;
      })
      .addCase(forgotPassword.fulfilled, (state, _) => {
        state.forgotPasswordError = false;
        state.forgotPasswordSuccess = true;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        const error = get(action, 'error');

        state.forgotPasswordSuccess = false;
        state.forgotPasswordError = Cognito.getCognitoErrorMessage(error);
      })
      .addCase(resendVerificationEmail.fulfilled, (state, _) => {
        state.resendVerificationEmailError = false;
        state.resendVerificationEmailSuccess = true;
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        const error = get(action, 'error');

        state.resendVerificationEmailSuccess = false;
        state.resendVerificationEmailError = Cognito.getCognitoErrorMessage(
          error,
        );
      })
      .addCase(changePassword.fulfilled, (state, _) => {
        state.changePasswordError = false;
        state.changePasswordSuccess = true;
      })
      .addCase(changePassword.rejected, (state, action) => {
        const error = get(action, 'error');
        state.changePasswordSuccess = false;
        state.changePasswordError = Cognito.getCognitoErrorMessage(
          error,
          false,
          true,
        );
      })
      .addCase(verifyEmail.fulfilled, (state, _) => {
        state.verificationError = false;
        state.verificationSuccess = true;
        state.pendingVerification = false;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        const error = get(action, 'error');
        if (error.code !== Cognito.CODES.NOT_AUTHORIZED) {
          state.verificationSuccess = false;
          state.verificationError = Cognito.getCognitoErrorMessage(error);
        } else {
          state.verificationError = false;
          state.verificationSuccess = true;
          state.pendingVerification = false;
        }
      })
      .addCase(uploadApprovalAudioClip.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(uploadApprovalAudioClip.rejected, (state, _) => {
        state.producerApprovalError =
          'Service unavailable please try again later';
        state.loading = false;
      })
      .addCase(PASSWORD_REQUIRED, state => {
        state.passwordResetRequired = true;
      })

      .addCase(PASSWORD_NOT_REQUIRED, state => {
        state.passwordResetRequired = false;
      })
      .addCase(USER_LOADED, (state, action) => {
        state.cognitoUser = action.payload;
      })
      .addCase(LOGIN_SUCCESS, state => {
        state.loading = false;
        state.authenticated = true;
        state.pendingVerification = false;
      })
      .addCase(LOGIN_ERROR, (state, action) => {
        const error = get(action, 'error');
        const errorCode = get(error, 'code');

        state.loading = false;
        state.authenticated = false;

        if (errorCode === Cognito.CODES.USER_NOT_CONFIRMED) {
          state.pendingVerification = true;
          state.loginError = false;
          return;
        }

        state.pendingVerification = false;
        state.loginError = Cognito.getCognitoErrorMessage(error);
      });
  },
  selectors: {
    getIsAuthenticated: state => state.authenticated,
    getLoginError: state => state.loginError,
    getSignupError: state => state.signupError,
    getSignupSuccess: state => state.signupSuccess,
    getProducerApprovalError: state => state.producerApprovalError,
    getProducerApprovalSuccess: state => state.producerApprovalSuccess,
    getChangePasswordError: state => state.changePasswordError,
    getChangePasswordSuccess: state => state.changePasswordSuccess,
    getResetPasswordError: state => state.resetPasswordError,
    getResetPasswordSuccess: state => state.resetPasswordSuccess,
    getCompleteNewPasswordError: state => state.completeNewPasswordError,
    getCompleteNewPasswordSuccess: state => state.completeNewPasswordSuccess,
    getForgotPasswordError: state => state.forgotPasswordError,
    getForgotPasswordSuccess: state => state.forgotPasswordSuccess,
    getResendVerificationEmailError: state =>
      state.resendVerificationEmailError,
    getResendVerificationEmailSuccess: state =>
      state.resendVerificationEmailSuccess,
    getVerificationError: state => state.verificationError,
    getVerificationSuccess: state => state.verificationSuccess,
    getPendingVerification: state => state.pendingVerification,
    getPasswordResetRequired: state => state.passwordResetRequired,
    getCognitoUser: state => state.cognitoUser,
    getCognitoAccessToken: state =>
      state.cognitoUser.signInUserSession.accessToken.jwtToken,
    getLoading: state => state.loading,
  },
});

export const reducer = authSlice.reducer;
export const {
  clearSignUpStatus,
  clearResetPasswordStatus,
  clearForgotPasswordStatus,
  clearChangePasswordStatus,
  clearResendVerificationEmailStatus,
} = authSlice.actions;
export const {
  getIsAuthenticated,
  getLoginError,
  getSignupError,
  getSignupSuccess,
  getProducerApprovalError,
  getProducerApprovalSuccess,
  getChangePasswordError,
  getChangePasswordSuccess,
  getResetPasswordError,
  getResetPasswordSuccess,
  getCompleteNewPasswordError,
  getCompleteNewPasswordSuccess,
  getForgotPasswordError,
  getForgotPasswordSuccess,
  getResendVerificationEmailError,
  getResendVerificationEmailSuccess,
  getVerificationError,
  getVerificationSuccess,
  getPendingVerification,
  getPasswordResetRequired,
  getCognitoUser,
  getCognitoAccessToken,
  getLoading,
} = authSlice.selectors;
